import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Team, ModuleId, DEFAULT_MODULE_ORDER } from '@/types/team';
import { isAuthRequired } from '@/lib/environment';
import { toast } from '@/lib/toast';
import { dbRowToTeam, mapTeamToDb, TEAM_COLUMNS, TEAM_COLUMNS_LEGACY } from '@/logic/teamMapper';

// Get current user id for team creation
const getCurrentUserId = async (): Promise<string | null> => {
  // Prefer session (local) over getUser() (network) to be resilient in PWA/offline-ish states
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.user?.id) return session.user.id;

  const { data: { user } } = await supabase.auth.getUser();
  return user?.id || null;
};

// Returns true when a Supabase error indicates an undefined DB column (PostgreSQL
// error code 42703). This is the specific failure mode that caused the 2026-06-12
// incident: a select with a not-yet-migrated column returns HTTP 400 + code 42703.
const isUndefinedColumnError = (err: any): boolean =>
  err?.code === '42703' || (typeof err?.message === 'string' && err.message.includes('42703'));

/**
 * Resilient teams select: tries TEAM_COLUMNS first; if the DB returns a
 * 400/42703 (undefined column — pending migration), retries once with
 * TEAM_COLUMNS_LEGACY. On retry success, logs a console.warn so it is visible
 * in prod DevTools without being noisy in the normal path.
 */
async function selectTeamsResilient(
  buildQuery: (columns: string) => PromiseLike<{ data: any[] | null; error: any }>
): Promise<{ data: any[] | null; error: any }> {
  const first = await buildQuery(TEAM_COLUMNS);
  if (first.error && isUndefinedColumnError(first.error)) {
    console.warn('teams select fell back to legacy columns — pending migration?');
    return buildQuery(TEAM_COLUMNS_LEGACY);
  }
  return first;
}

const CURRENT_TEAM_KEY = 'team-v-does-current-team'; // Team storage key
const DEFAULT_TEAM_KEY = 'team-v-does-default-team'; // Configured default team key
const TEAMS_CACHE_KEY = 'team-v-does-teams-cache'; // Teams cache key
const TEAMS_DEV_CACHE_KEY = 'team-v-does-teams-cache-dev'; // Dev fallback cache
const LAST_USER_ID_KEY = 'team-v-does-last-user-id'; // Used to prevent cross-user cache flash
const TASKS_CACHE_PREFIX = 'team-v-does-tasks-cache:'; // Must match useTasks
const TEAM_MEMBERS_BLOCKED_KEY = 'team-v-does-team-members-blocked';

type TeamsCachePayload = {
  v: 1;
  userId: string | null;
  teams: any[];
};

const loadTeamsCachePayload = (cacheKey: string): TeamsCachePayload | null => {
  try {
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;

    const parsed = JSON.parse(cached);
    if (!parsed || typeof parsed !== 'object') return null;

    // Backwards compatibility: older versions stored just Team[]
    if (Array.isArray(parsed)) {
      return { v: 1, userId: null, teams: parsed };
    }

    if (parsed.v !== 1 || !Array.isArray(parsed.teams)) return null;
    return {
      v: 1,
      userId: typeof parsed.userId === 'string' ? parsed.userId : null,
      teams: parsed.teams,
    };
  } catch {
    return null;
  }
};

const getCachedTeamsUserId = (): string | null => {
  return loadTeamsCachePayload(TEAMS_CACHE_KEY)?.userId ?? null;
};

// Load teams from localStorage cache
const loadTeamsFromCache = (): Team[] => {
  if (isAuthRequired()) {
    try {
      const lastUserId = localStorage.getItem(LAST_USER_ID_KEY);
      if (!lastUserId) return [];
    } catch {
      return [];
    }
  }
  const payload =
    loadTeamsCachePayload(TEAMS_CACHE_KEY) ||
    (!isAuthRequired() ? loadTeamsCachePayload(TEAMS_DEV_CACHE_KEY) : null);
  if (!payload) return [];

  try {
    // Restore Date objects
    return payload.teams.map((t: any) => ({ ...t, createdAt: new Date(t.createdAt) }));
  } catch {
    return [];
  }
};

// Save teams to localStorage cache
const saveTeamsToCache = (teams: Team[], userId: string | null) => {
  try {
    const payload: TeamsCachePayload = {
      v: 1,
      userId,
      teams,
    };
    localStorage.setItem(TEAMS_CACHE_KEY, JSON.stringify(payload));
    if (!isAuthRequired()) {
      localStorage.setItem(TEAMS_DEV_CACHE_KEY, JSON.stringify(payload));
    }
  } catch {
    // ignore
  }
};

export function useTeams() {
  const initialTeamMembersBlocked = (() => {
    try {
      return localStorage.getItem(TEAM_MEMBERS_BLOCKED_KEY) === '1';
    } catch {
      return false;
    }
  })();
  // Initialize from localStorage cache so UI is stable on refresh
  const [teams, setTeamsState] = useState<Team[]>(() => loadTeamsFromCache());
  const [currentTeamId, setCurrentTeamIdState] = useState<string | null>(() => {
    return localStorage.getItem(DEFAULT_TEAM_KEY) || localStorage.getItem(CURRENT_TEAM_KEY);
  });
  // Only show loading if we have no cached data
  const [loading, setLoading] = useState(() => loadTeamsFromCache().length === 0);
  // loadFailed is true when teams could not be fetched due to a non-network error
  // (e.g. a 400/42703 missing-column error even after the legacy-column retry).
  // The onboarding gate MUST check this: an empty teams array due to a fetch
  // failure must NOT be treated as "new user with no teams".
  const [loadFailed, setLoadFailed] = useState(false);

  const [userId, setUserId] = useState<string | null>(null);
  const [teamMemberships, setTeamMemberships] = useState<{ teamId: string; role: 'admin' | 'member' }[]>([]);
  const teamMembersBlockedRef = useRef(initialTeamMembersBlocked);
  const teamMembersErrorLoggedRef = useRef(false);
  const isFetchingRef = useRef(false);

  // Wrapper that also persists to cache
  const setTeams = useCallback(
    (updater: Team[] | ((prev: Team[]) => Team[])) => {
      setTeamsState((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        saveTeamsToCache(next, userId);
        return next;
      });
    },
    [userId]
  );

  // Track auth state
  useEffect(() => {
    const loadLastUserId = (): string | null => {
      try {
        return localStorage.getItem(LAST_USER_ID_KEY);
      } catch {
        return null;
      }
    };

    const persistLastUserId = (id: string | null) => {
      try {
        if (id) localStorage.setItem(LAST_USER_ID_KEY, id);
        else localStorage.removeItem(LAST_USER_ID_KEY);
      } catch {
        // ignore
      }
    };

    const clearPersistedTeamState = () => {
      try {
        localStorage.removeItem(CURRENT_TEAM_KEY);
        localStorage.removeItem(DEFAULT_TEAM_KEY);
        localStorage.removeItem(TEAMS_CACHE_KEY);

        for (let i = localStorage.length - 1; i >= 0; i--) {
          const k = localStorage.key(i);
          if (k && k.startsWith(TASKS_CACHE_PREFIX)) {
            localStorage.removeItem(k);
          }
        }
      } catch {
        // ignore
      }
    };

    // Seed from last-known user OR from cache payload to prevent a null→user flash on refresh.
    let previousUserId: string | null = loadLastUserId() ?? getCachedTeamsUserId();

    const getUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const nextUserId = session?.user?.id || null;

      if (nextUserId) {
        previousUserId = nextUserId;
        persistLastUserId(nextUserId);
        setUserId(nextUserId);
        return;
      }

      // Keep the last-known user during optimistic auth restoration.
      if (!previousUserId) {
        persistLastUserId(null);
        setUserId(null);
      }
    };
    getUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      const nextUserId = session?.user?.id || null;
      const effectivePrevUserId = previousUserId ?? getCachedTeamsUserId() ?? loadLastUserId();
      const explicitSignOut = event === 'SIGNED_OUT';
      const switchedUser = !!effectivePrevUserId && !!nextUserId && effectivePrevUserId !== nextUserId;

      if (explicitSignOut || switchedUser) {
        clearPersistedTeamState();
        setCurrentTeamIdState(null);
        setTeamsState([]);
      }

      if (nextUserId) {
        previousUserId = nextUserId;
        persistLastUserId(nextUserId);
        setUserId(nextUserId);
        return;
      }

      if (explicitSignOut) {
        previousUserId = null;
        persistLastUserId(null);
        setUserId(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Clear cache on auth change, but keep loading state accurate
  const fetchTeams = useCallback(async (isBackgroundRefresh = false) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    const preserveVisibleState = isBackgroundRefresh || teams.length > 0;

    // Helper to ensure loading is cleared
    const finish = () => {
      if (!isBackgroundRefresh) setLoading(false);
    };

    try {
      if (!isBackgroundRefresh && teams.length === 0) {
        setLoading(true);
      }

      // Inner try for the actual work
      try {
        // Get current user
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        const user = session?.user ?? null;
        if (sessionError) {
          console.warn('[useTeams] getSession error:', sessionError);
        }

        const DEVICE_AUTHORIZED_TEAM_KEY = 'team_v_authorized_team';
        const authorizedTeamId = (() => {
          try {
            return localStorage.getItem(DEVICE_AUTHORIZED_TEAM_KEY);
          } catch {
            return null;
          }
        })();

        if (!user) {
          if (!isAuthRequired()) {
            const { data, error } = await selectTeamsResilient((cols) =>
              supabase.from('teams').select(cols).order('name')
            );
            if (error) {
              console.error('Error fetching teams (dev mode):', error);
              return;
            }
            setTeams(data?.map(dbRowToTeam) || []);
            return;
          }

          if (authorizedTeamId) {
            let teamData: any = null;
            // Resilient single-row fetch: try TEAM_COLUMNS, fall back to legacy on 42703.
            let directError: any = null;
            {
              const r1 = await supabase.from('teams').select(TEAM_COLUMNS).eq('id', authorizedTeamId).maybeSingle();
              if (r1.error && isUndefinedColumnError(r1.error)) {
                console.warn('teams select fell back to legacy columns — pending migration?');
                const r2 = await supabase.from('teams').select(TEAM_COLUMNS_LEGACY).eq('id', authorizedTeamId).maybeSingle();
                directError = r2.error;
                teamData = r2.data;
              } else {
                directError = r1.error;
                teamData = r1.data;
              }
            }

            if (directError) {
              console.error('Error fetching authorized team natively:', directError);
            }

            // Fallback to RPC if anon RLS blocked the read
            if (!teamData) {
              const rpcResult = await supabase.rpc('get_team_by_id', { _team_id: authorizedTeamId });
              if (rpcResult.error || !rpcResult.data) {
                console.error('Error fetching authorized team via RPC:', rpcResult.error);
                if (!preserveVisibleState) {
                  setTeamMemberships([]);
                  setTeams([]);
                  setCurrentTeamIdState(null);
                }
                return;
              }
              teamData = rpcResult.data;
            }

            setTeams([dbRowToTeam(teamData)]);
            setCurrentTeamIdState(authorizedTeamId);
            return;
          }

          if (!preserveVisibleState) {
            setTeamMemberships([]);
            setTeams([]);
            setCurrentTeamIdState(null);
          }
          return;
        }

        const userEmail = (user.email || '').trim().toLowerCase();
        if (userEmail) {
          const { data: unlinkEmails } = await supabase
            .from('family_member_emails')
            .select('id')
            .is('user_id', null)
            .ilike('email', userEmail);

          if (unlinkEmails && unlinkEmails.length > 0) {
            await supabase
              .from('family_member_emails')
              .update({ user_id: user.id })
              .in('id', unlinkEmails.map(e => e.id));
          }

          await supabase
            .from('family_members')
            .update({ user_id: user.id })
            .is('user_id', null)
            .ilike('email', userEmail);
        }

        const memberPromise = teamMembersBlockedRef.current
          ? Promise.resolve({ data: [], error: null })
          : supabase.from('team_members').select('team_id, role').eq('user_id', user.id);

        const [
          { data: memberData, error: memberError },
          ownedRes,
          { data: familyLinksData, error: familyError },
          { data: emailLinksData, error: emailError },
          { data: profileData, error: profileError },
        ] = await Promise.all([
          memberPromise,
          selectTeamsResilient((cols) =>
            supabase.from('teams').select(cols).eq('created_by_user_id', user.id)
          ),
          supabase.from('family_members').select('team_id').eq('user_id', user.id).not('team_id', 'is', null),
          supabase.from('family_member_emails').select('team_id').eq('user_id', user.id),
          supabase.from('profiles').select('default_team_id').eq('user_id', user.id).maybeSingle(),
        ]);
        const ownedData = ownedRes.data;
        const ownedError = ownedRes.error;

        const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;
        const hasNetworkError = (err: any) => err?.message === 'Failed to fetch' || err?.message?.includes('Network Error');

        if (
          isOffline ||
          hasNetworkError(memberError) ||
          hasNetworkError(ownedError) ||
          hasNetworkError(familyError) ||
          hasNetworkError(emailError) ||
          hasNetworkError(profileError)
        ) {
          console.warn('[useTeams] Offline or network error detected, keeping cached teams');
          return;
        }


        if (memberError?.code === '42P17') {
          teamMembersBlockedRef.current = true;
          try { localStorage.setItem(TEAM_MEMBERS_BLOCKED_KEY, '1'); } catch { }
        }

        const ownedTeamIds = ((ownedData as Array<{ id: string }> | null) || []).map(t => t.id);
        const familyTeamIds = (familyLinksData?.map(r => r.team_id).filter(Boolean) as string[]) || [];
        const emailTeamIds = (emailLinksData?.map(r => r.team_id).filter(Boolean) as string[]) || [];
        const allFamilyTeamIds = Array.from(new Set([...familyTeamIds, ...emailTeamIds]));
        const memberTeamIds = (memberData?.map(m => m.team_id).filter(Boolean) as string[]) || [];

        const allTeamIds = Array.from(new Set([...memberTeamIds, ...ownedTeamIds, ...allFamilyTeamIds]));

        if (allTeamIds.length === 0) {
          if (!preserveVisibleState) {
            setTeamMemberships([]);
            setTeams([]);
            setCurrentTeamIdState(null);
          }
          return;
        }

        const { data: teamsData, error: teamsError } = await selectTeamsResilient((cols) =>
          supabase.from('teams').select(cols).in('id', allTeamIds).order('name')
        );

        if (teamsError) {
          console.error('Error fetching teams:', teamsError);
          if (isOffline || hasNetworkError(teamsError)) {
            console.warn('[useTeams] Offline or network error fetching teams, keeping cached teams');
          } else {
            // A real (non-network) error after the legacy-column retry means the
            // fetch genuinely failed. Mark loadFailed so the onboarding gate does
            // NOT treat this as "new user with no teams".
            setLoadFailed(true);
            if (!preserveVisibleState) {
              setTeams([]);
            }
          }
          return;
        }
        setLoadFailed(false);

        if (!teamMembersBlockedRef.current && memberData) {
          setTeamMemberships(memberData.map(m => ({ teamId: m.team_id, role: m.role as 'admin' | 'member' })));
        }

        // Restore default team from profile if missing locally
        if (profileData?.default_team_id && allTeamIds.includes(profileData.default_team_id)) {
          try {
            if (!localStorage.getItem(DEFAULT_TEAM_KEY)) {
              localStorage.setItem(DEFAULT_TEAM_KEY, profileData.default_team_id);
            }
          } catch { }
        }

        const nextTeams = teamsData?.map(dbRowToTeam) || [];
        if (nextTeams.length === 0 && preserveVisibleState) {
          return;
        }

        setTeams(nextTeams);
        console.log('[useTeams] fetchTeams success');
      } catch (err) {
        console.error('[useTeams] fetchTeams inner error:', err);
      } finally {
        finish();
      }
    } catch (err) {
      console.error('[useTeams] fetchTeams outer error:', err);
    } finally {
      console.log('[useTeams] fetchTeams finished');
      isFetchingRef.current = false;
    }
  }, [setTeams, teams.length]);

  const lastTeamsFetchRef = useRef<number>(0);

  useEffect(() => {
    // Initial fetch - show loading only if no cache
    fetchTeams(false).then(() => { lastTeamsFetchRef.current = Date.now(); });

    // Refetch when auth state changes (background refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      fetchTeams(true).then(() => { lastTeamsFetchRef.current = Date.now(); });
    });

    // Realtime removed — teams/team_members change very rarely.
    // Throttled refetch on tab focus (15 min min interval).
    const TEAMS_REFETCH_MIN_INTERVAL_MS = 15 * 60 * 1000;
    const onVisible = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        if (Date.now() - lastTeamsFetchRef.current < TEAMS_REFETCH_MIN_INTERVAL_MS) return;
        fetchTeams(true).then(() => { lastTeamsFetchRef.current = Date.now(); });
      }
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisible);
    }

    return () => {
      subscription.unsubscribe();
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisible);
      }
    };
  }, [fetchTeams]);

  const setCurrentTeamId = useCallback((id: string | null) => {
    if (id) {
      localStorage.setItem(CURRENT_TEAM_KEY, id);
    } else {
      localStorage.removeItem(CURRENT_TEAM_KEY);
    }
    setCurrentTeamIdState(id);
  }, []);

  // Auto-select first available team when no team is selected
  useEffect(() => {
    if (!loading && teams.length === 0 && currentTeamId) {
      setCurrentTeamId(null);
      return;
    }

    if (!loading && teams.length > 0 && !currentTeamId) {
      const defaultTeamId = localStorage.getItem(DEFAULT_TEAM_KEY);
      if (defaultTeamId && teams.find(t => t.id === defaultTeamId)) {
        setCurrentTeamId(defaultTeamId);
      } else {
        // Select first available team
        setCurrentTeamId(teams[0].id);
      }
    }
    // Clear current team if it's no longer accessible
    if (!loading && currentTeamId && teams.length > 0 && !teams.find(t => t.id === currentTeamId)) {
      const defaultTeamId = localStorage.getItem(DEFAULT_TEAM_KEY);
      if (defaultTeamId && teams.find(t => t.id === defaultTeamId)) {
        setCurrentTeamId(defaultTeamId);
      } else {
        setCurrentTeamId(teams[0].id);
      }
    }
  }, [loading, teams, currentTeamId, setCurrentTeamId]);

  // Team activity heartbeat: update last_active_at every 5 minutes
  useEffect(() => {
    if (!currentTeamId || !userId) return;

    const sendHeartbeat = async () => {
      try {
        await supabase
          .from('team_members')
          .update({ last_active_at: new Date().toISOString() })
          .eq('team_id', currentTeamId)
          .eq('user_id', userId);
      } catch (err) {
        // Silently ignore heartbeat failures
        console.debug('[useTeams] Heartbeat failed:', err);
      }
    };

    // Fire immediately on team switch
    sendHeartbeat();

    // Then every 5 minutes
    const interval = setInterval(sendHeartbeat, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [currentTeamId, userId]);

  const addTeam = useCallback(async (name: string): Promise<string | null> => {
    const trimmedName = name.trim();
    if (!trimmedName) return null;

    // Get current user for creator tracking
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    const currentUserId = user?.id || null;

    // RLS requires authenticated user - prevent creation when not logged in
    if (!currentUserId) {
      toast.error('Je moet ingelogd zijn om een team aan te maken', { channel: 'teams' });
      return null;
    }

    // Create default slogan for new team
    const defaultSlogan = `${trimmedName} Does`;

    const { data, error } = await supabase
      .from('teams')
      .insert(mapTeamToDb({
        name: trimmedName,
        slogan: defaultSlogan,
        createdByUserId: currentUserId,
      }))
      .select()
      .single();

    if (error) {
      if (error.code === '23505') {
        toast.error('Deze teamnaam bestaat al', { channel: 'teams' });
      } else {
        console.error('Error adding team:', error);
        toast.error('Fout bij toevoegen van team', { channel: 'teams' });
      }
      return null;
    }

    // Automatically add user as team admin
    const { error: memberError } = await supabase
      .from('team_members')
      .insert({
        team_id: data.id,
        user_id: currentUserId,
        role: 'admin',
      });

    if (memberError && memberError.code !== '23505') {
      console.error('Error adding team creator as admin:', memberError);
    }

    // Create a family_members entry for the team creator so they appear in the team member list
    const displayName = user?.user_metadata?.full_name
      || user?.user_metadata?.name
      || user?.email?.split('@')[0]
      || 'Eigenaar';

    const { data: familyMemberData, error: familyError } = await supabase
      .from('family_members')
      .insert({
        name: displayName,
        team_id: data.id,
        user_id: currentUserId,
        email: user?.email || null,
      })
      .select()
      .single();

    if (familyError) {
      console.error('Error creating family member for team creator:', familyError);
    } else if (familyMemberData && user?.email) {
      // Also add the email to family_member_emails for the new multi-email system
      const { error: emailError } = await supabase
        .from('family_member_emails')
        .insert({
          family_member_id: familyMemberData.id,
          team_id: data.id,
          email: user.email.toLowerCase(),
          user_id: currentUserId,
          is_primary: true,
        });

      if (emailError && emailError.code !== '23505') {
        console.error('Error adding creator email to family_member_emails:', emailError);
      }
    }

    // Optimistically add to local state immediately
    const newTeam = dbRowToTeam(data);
    setTeams(prev => [...prev, newTeam].sort((a, b) => a.name.localeCompare(b.name)));

    toast.success(`Team "${trimmedName}" aangemaakt!`, { channel: 'teams' });
    return data.id;
  }, [setTeams]);

  const updateTeamSlogan = useCallback(async (teamId: string, slogan: string): Promise<void> => {
    const { error } = await supabase
      .from('teams')
      .update(mapTeamToDb({ slogan }))
      .eq('id', teamId);

    if (error) {
      console.error('Error updating team slogan:', error);
      toast.error('Fout bij bijwerken van slogan', { channel: 'teams' });
      return;
    }

    setTeams(prev => prev.map(t =>
      t.id === teamId ? { ...t, slogan } : t
    ));

    toast.success('Slogan bijgewerkt!', { channel: 'teams' });
  }, [setTeams]);

  const updateTeamName = useCallback(async (teamId: string, name: string): Promise<void> => {
    const trimmedName = name.trim();
    if (!trimmedName) return;

    const { error } = await supabase
      .from('teams')
      .update(mapTeamToDb({ name: trimmedName }))
      .eq('id', teamId);

    if (error) {
      if (error.code === '23505') {
        toast.error('Deze teamnaam bestaat al', { channel: 'teams' });
      } else {
        console.error('Error updating team name:', error);
        toast.error('Fout bij bijwerken van teamnaam', { channel: 'teams' });
      }
      return;
    }

    setTeams(prev => prev.map(t =>
      t.id === teamId ? { ...t, name: trimmedName } : t
    ).sort((a, b) => a.name.localeCompare(b.name)));

    toast.success('Teamnaam bijgewerkt!', { channel: 'teams' });
  }, [setTeams]);

  const updateTeamImage = useCallback(async (teamId: string, imageUrl: string | null): Promise<void> => {
    const { error } = await supabase
      .from('teams')
      .update(mapTeamToDb({ imageUrl }))
      .eq('id', teamId);

    if (error) {
      console.error('Error updating team image:', error);
      toast.error('Fout bij bijwerken van teamfoto');
      return;
    }

    setTeams(prev => prev.map(t =>
      t.id === teamId ? { ...t, imageUrl } : t
    ));

    toast.success('Teamfoto bijgewerkt!');
  }, [setTeams]);

  const updateWeekendWindow = useCallback(async (
    teamId: string,
    settings: {
      weekendStartDay?: number;
      weekendStartHour?: number;
      weekendEndDay?: number;
      weekendEndHour?: number;
    }
  ): Promise<void> => {
    const { error } = await supabase
      .from('teams')
      .update(mapTeamToDb(settings))
      .eq('id', teamId);

    if (error) {
      console.error('Error updating weekend window:', error);
      toast.error('Fout bij bijwerken van weekend-instellingen');
      return;
    }

    setTeams(prev => prev.map(t =>
      t.id === teamId ? { ...t, ...settings } : t
    ));

    toast.success('Weekend-instellingen bijgewerkt!');
  }, [setTeams]);

  const updateTeamNiche = useCallback(async (teamId: string, niche: import('@/types/team').ClanNiche): Promise<void> => {
    // niche IS a DB column (migration 20260612000001; MCP team.update supports
    // it) — persist server-side so the clan's face is shared across members
    // and settable by agents. Until LOV-9 applies the migration in prod the
    // UPDATE fails on the missing column: degrade to in-memory + warn instead
    // of blocking the UI (optimistic state keeps the right face this session).
    setTeams(prev => prev.map(t => t.id === teamId ? { ...t, niche } : t));
    const { error } = await supabase
      .from('teams')
      .update({ niche } as never)
      .eq('id', teamId);
    if (error) {
      console.warn('teams.niche persist failed (migration pending? LOV-9):', error.message);
      toast.success('Clan-type lokaal bijgewerkt (synchronisatie volgt)', { id: 'team_niche_updated', channel: 'teams' });
      return;
    }
    toast.success('Clan-type bijgewerkt!', { id: 'team_niche_updated', channel: 'teams' });
  }, [setTeams]);

  const updateTeamFreelancerEnabled = useCallback(async (teamId: string, enabled: boolean): Promise<void> => {
    const { error } = await supabase
      .from('teams')
      .update(mapTeamToDb({ freelancerEnabled: enabled }))
      .eq('id', teamId);

    if (error) {
      console.error('Error updating freelancer flag:', error);
      toast.error('Kon freelancer-instelling niet opslaan (teambeheerder vereist?)');
      return;
    }

    setTeams(prev => prev.map(t =>
      t.id === teamId ? { ...t, freelancerEnabled: enabled } : t
    ));
  }, [setTeams]);

  const updateDayPeriodHours = useCallback(async (
    teamId: string,
    settings: {
      morningHour?: number;
      eveningHour?: number;
    }
  ): Promise<void> => {
    const { error } = await supabase
      .from('teams')
      .update(mapTeamToDb(settings))
      .eq('id', teamId);

    if (error) {
      console.error('Error updating day period hours:', error);
      toast.error('Fout bij bijwerken van dagdeel-instellingen');
      return;
    }

    setTeams(prev => prev.map(t =>
      t.id === teamId ? { ...t, ...settings } : t
    ));

    toast.success('Dagdeel-instellingen bijgewerkt!');
  }, [setTeams]);

  const updateTeamSlug = useCallback(async (teamId: string, slug: string): Promise<boolean> => {
    const trimmedSlug = slug.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-');
    if (!trimmedSlug) {
      toast.error('URL-naam mag niet leeg zijn');
      return false;
    }

    // Check if slug is already taken by another team
    const { data: existing } = await supabase
      .from('teams')
      .select('id')
      .eq('slug', trimmedSlug)
      .neq('id', teamId)
      .single();

    if (existing) {
      toast.error('Deze URL-naam is al in gebruik door een ander team');
      return false;
    }

    const { error } = await supabase
      .from('teams')
      .update(mapTeamToDb({ slug: trimmedSlug }))
      .eq('id', teamId);

    if (error) {
      if (error.code === '23505') {
        toast.error('Deze URL-naam is al in gebruik');
      } else {
        console.error('Error updating team slug:', error);
        toast.error('Fout bij bijwerken van URL-naam');
      }
      return false;
    }

    setTeams(prev => prev.map(t =>
      t.id === teamId ? { ...t, slug: trimmedSlug } : t
    ));

    toast.success('URL-naam bijgewerkt!');
    return true;
  }, [setTeams]);

  const updateModuleVisibility = useCallback(async (
    teamId: string,
    settings: {
      showFypModule?: boolean;
      showTasksModule?: boolean;
      showCompletedModule?: boolean;
      showGroceryModule?: boolean;
      showStockModule?: boolean;
      showMealsModule?: boolean;
      showHealthModule?: boolean;
      showGoalsModule?: boolean;
      showAgentsModule?: boolean;
      showBoardgamesModule?: boolean;
    }
  ): Promise<void> => {
    const { error } = await supabase
      .from('teams')
      .update(mapTeamToDb(settings))
      .eq('id', teamId);

    if (error) {
      console.error('Error updating module visibility:', error);
      toast.error('Fout bij bijwerken van module-instellingen', { channel: 'teams' });
      return;
    }

    setTeams(prev => prev.map(t =>
      t.id === teamId ? { ...t, ...settings } : t
    ));

    toast.success('Module-instellingen bijgewerkt!', { channel: 'teams' });
  }, [setTeams]);

  const updateTeamTaskListLayout = useCallback(async (
    teamId: string,
    layout: import('@/types/taskListLayout').TaskListSectionEntry[] | null
  ): Promise<boolean> => {
    const { error } = await supabase
      .from('teams')
      .update(mapTeamToDb({ taskListLayout: layout }))
      .eq('id', teamId);

    if (error) {
      console.error('Error updating task list layout:', error);
      toast.error('Could not save task list layout', { channel: 'teams' });
      return false;
    }

    setTeams((prev) =>
      prev.map((t) => (t.id === teamId ? { ...t, taskListLayout: layout } : t))
    );
    toast.success('Task list layout saved', { channel: 'teams' });
    return true;
  }, [setTeams]);

  const updateModuleOrder = useCallback(async (
    teamId: string,
    moduleOrder: ModuleId[]
  ): Promise<void> => {
    const { error } = await supabase
      .from('teams')
      .update(mapTeamToDb({ moduleOrder }))
      .eq('id', teamId);

    if (error) {
      console.error('Error updating module order:', error);
      toast.error('Fout bij bijwerken van module-volgorde', { channel: 'teams' });
      return;
    }

    setTeams(prev => prev.map(t =>
      t.id === teamId ? { ...t, moduleOrder } : t
    ));

    toast.success('Module-volgorde bijgewerkt!', { channel: 'teams' });
  }, [setTeams]);

  const updateTeamModuleLabels = useCallback(async (
    teamId: string,
    moduleLabels: Team['moduleLabels']
  ): Promise<void> => {
    // Store in localStorage since DB column doesn't exist yet
    localStorage.setItem(`team-module-labels-${teamId}`, JSON.stringify(moduleLabels || {}));

    setTeams(prev => prev.map(t =>
      t.id === teamId ? { ...t, moduleLabels: moduleLabels || {} } : t
    ));

    toast.success('Module labels bijgewerkt!', { channel: 'teams' });
  }, [setTeams]);

  const updateTeamTagIcons = useCallback(async (
    teamId: string,
    tagIcons: Team['tagIcons']
  ): Promise<void> => {
    // Store in localStorage since DB column doesn't exist yet
    localStorage.setItem(`team-tag-icons-${teamId}`, JSON.stringify(tagIcons || {}));

    setTeams(prev => prev.map(t =>
      t.id === teamId ? { ...t, tagIcons: tagIcons || {} } : t
    ));

    toast.success('Tag iconen bijgewerkt!', { channel: 'teams' });
  }, [setTeams]);

  const updateTeamVisibility = useCallback(async (teamId: string, isPublic: boolean): Promise<void> => {
    const { error } = await supabase
      .from('teams')
      .update(mapTeamToDb({ isPublic }))
      .eq('id', teamId);

    if (error) {
      console.error('Error updating team visibility:', error);
      toast.error('Fout bij bijwerken van team zichtbaarheid', { channel: 'teams' });
      return;
    }

    setTeams(prev => prev.map(t =>
      t.id === teamId ? { ...t, isPublic } : t
    ));

    toast.success(isPublic ? 'Team is nu openbaar!' : 'Team is nu privé!', { channel: 'teams' });
  }, [setTeams]);

  const updateInviteCode = useCallback(async (teamId: string, code: string): Promise<boolean> => {
    const trimmedCode = code.trim().toUpperCase();
    if (!trimmedCode) {
      toast.error('Code mag niet leeg zijn', { channel: 'teams' });
      return false;
    }

    const { error } = await supabase
      .from('teams')
      .update(mapTeamToDb({ inviteCode: trimmedCode }))
      .eq('id', teamId);

    if (error) {
      if (error.code === '23505') {
        toast.error('Deze code is al in gebruik', { channel: 'teams' });
      } else {
        console.error('Error updating invite code:', error);
        toast.error('Fout bij bijwerken van uitnodigingscode', { channel: 'teams' });
      }
      return false;
    }

    setTeams(prev => prev.map(t =>
      t.id === teamId ? { ...t, inviteCode: trimmedCode } : t
    ));
    toast.success('Uitnodigingscode bijgewerkt!', { channel: 'teams' });
    return true;
  }, [setTeams]);

  const updateInviteCodeEnabled = useCallback(async (teamId: string, enabled: boolean): Promise<void> => {
    const { error } = await supabase
      .from('teams')
      .update(mapTeamToDb({ inviteCodeEnabled: enabled }))
      .eq('id', teamId);

    if (error) {
      console.error('Error updating invite code enabled:', error);
      toast.error('Fout bij bijwerken van uitnodigingscode', { channel: 'teams' });
      return;
    }

    setTeams(prev => prev.map(t =>
      t.id === teamId ? { ...t, inviteCodeEnabled: enabled } : t
    ));
    toast.success(enabled ? 'Uitnodigingscode geactiveerd!' : 'Uitnodigingscode gedeactiveerd!', { channel: 'teams' });
  }, [setTeams]);

  const joinTeamByCode = useCallback(async (code: string): Promise<{ success: boolean; teamId?: string; teamName?: string; alreadyMember?: boolean }> => {
    const { data, error } = await supabase.rpc('join_team_by_invite_code', { _code: code.trim() });

    if (error) {
      console.error('Error joining team by code:', error);
      toast.error('Fout bij het toetreden tot team', { channel: 'teams' });
      return { success: false };
    }

    const result = data as any;
    if (!result.success) {
      toast.error(result.error === 'Invalid or disabled invite code' ? 'Ongeldige of uitgeschakelde code' : result.error, { channel: 'teams' });
      return { success: false };
    }

    if (result.already_member) {
      toast.info(`Je bent al lid van ${result.team_name}`, { channel: 'teams' });
    } else {
      toast.success(`Welkom bij ${result.team_name}!`, { channel: 'teams' });
    }

    // Refresh teams
    await fetchTeams(true);
    return { success: true, teamId: result.team_id, teamName: result.team_name, alreadyMember: result.already_member };
  }, [fetchTeams]);

  const updateTeamLanguage = useCallback(async (
    teamId: string,
    language: 'nl' | 'en'
  ): Promise<void> => {
    const { error } = await supabase
      .from('teams')
      .update(mapTeamToDb({ language }))
      .eq('id', teamId);

    if (error) {
      console.error('Error updating team language:', error);
      toast.error('Fout bij bijwerken van taalinstellingen', { channel: 'teams' });
      return;
    }

    setTeams(prev => prev.map(t =>
      t.id === teamId ? { ...t, language } : t
    ));

    toast.success('Taalinstellingen bijgewerkt!', { channel: 'teams' });
  }, [setTeams]);

  const updateTeamTheme = useCallback(async (
    teamId: string,
    settings: {
      themePrimaryColor?: string;
      actionButtonBgColor?: string;
      actionButtonIconColor?: string;
    }
  ): Promise<void> => {
    const { error } = await supabase
      .from('teams')
      .update(mapTeamToDb(settings))
      .eq('id', teamId);

    if (error) {
      console.error('Error updating team theme:', error);
      toast.error('Fout bij bijwerken van thema-instellingen', { channel: 'teams' });
      return;
    }

    setTeams(prev => prev.map(t =>
      t.id === teamId ? { ...t, ...settings } : t
    ));
  }, [setTeams]);

  const currentTeam = teams.find(t => t.id === currentTeamId) || null;
  const isTeamMember = useCallback((teamId: string) => {
    return teamMemberships.some(tm => tm.teamId === teamId);
  }, [teamMemberships]);

  const isTeamAdmin = useCallback((teamId: string) => {
    if (teamMemberships.some(tm => tm.teamId === teamId && tm.role === 'admin')) {
      return true;
    }
    const team = teams.find(t => t.id === teamId);
    return !!(team && userId && team.createdByUserId === userId);
  }, [teamMemberships, teams, userId]);

  const searchPublicTeams = useCallback(async (query: string): Promise<Team[]> => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) return [];

    const { data, error } = await supabase
      .from('teams')
      .select(TEAM_COLUMNS)
      .eq('is_public', true)
      .ilike('name', `%${trimmedQuery}%`)
      .limit(10);

    if (error) {
      console.error('Error searching public teams:', error);
      toast.error('Fout bij zoeken naar teams', { channel: 'teams' });
      return [];
    }

    return (data || []).map(dbRowToTeam);
  }, []);

  const requestTeamAccess = useCallback(async (teamId: string): Promise<boolean> => {
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) {
      toast.error('Je moet ingelogd zijn om toegang tot een team te vragen', { channel: 'teams' });
      return false;
    }

    // Try to find the user's member profile to attach to the request if they have one
    const { data: profile } = await supabase
      .from('profiles')
      .select('member_id')
      .eq('user_id', user.id)
      .maybeSingle();

    const { error } = await supabase
      .from('approval_requests')
      .insert({
        team_id: teamId,
        requester_user_id: user.id,
        request_type: 'team_join',
        request_data: { member_id: profile?.member_id || null },
        status: 'pending'
      });

    if (error) {
      if (error.code === '23505') { // Unique violation
        toast.error('Je hebt al toegang aangevraagd voor dit team', { channel: 'teams' });
      } else {
        console.error('Error requesting team access:', error);
        toast.error('Fout bij aanvragen van toegang', { channel: 'teams' });
      }
      return false;
    }

    toast.success('Toegang aangevraagd! De beheerder van het team moet je verzoek nog goedkeuren.', { channel: 'teams' });
    return true;
  }, []);

  return {
    teams,
    teamMemberships,
    currentTeam,
    currentTeamId,
    setCurrentTeamId,
    addTeam,
    updateTeamSlogan,
    updateTeamName,
    updateTeamImage,
    updateTeamSlug,
    updateWeekendWindow,
    updateTeamFreelancerEnabled,
    updateTeamNiche,
    updateDayPeriodHours,
    updateModuleVisibility,
    updateTeamTaskListLayout,
    updateModuleOrder,
    updateTeamModuleLabels,
    updateTeamTagIcons,
    updateTeamLanguage,
    updateTeamVisibility,
    searchPublicTeams,
    requestTeamAccess,
    updateTeamTheme,
    updateInviteCode,
    updateInviteCodeEnabled,
    joinTeamByCode,
    isTeamAdmin,
    isTeamMember,
    loading,
    loadFailed,
    userId,
  };
}
